import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { selectSortedCommunicationTemplates } from '../../../store/communicationSelectors';
import { searchCommunicationTemplates } from '../../../store/communicationService';
import {
    loadExitDocumentTypes,
    loadOccThemeDetails,
    loadServiceDetailsForContractId,
    loadUploaderTypes
} from '../../../store/directusService';
import { deletePostcodeCsvData } from '../../../store/documentService';
import {
    loadContractConstants,
    searchEligibilityGroups,
    updateContractConstants
} from '../../../store/participantService';
import { loadFormsByContract } from '../../../store/participantService';
import { clearTeamsForService, loadTeamsForService } from '../../../store/userService';
import { updatePreSelects, updateSelectedItems } from '../../../utils/directusFunctions';
import { NO_CONTRACT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import { FORMS_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys } from '../../../utils/objectUtils';
import { hasRole, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import TextInputField from '../../formElements/TextInputField';
import LoadingSpinner from '../../ui/LoadingSpinner';
import ConfirmPrompt from '../../ui/notices/confirmPrompt/ConfirmPrompt';
import PostcodeUploader from '../../uploaders/postcodeUploader/PostcodeUploader';

import { initialErrorState, validate } from './validateFormsDefaultsConfigAdmin';

import form from '../../../commonStyles/formStyles.module.css';
import local from './formsDefaultsConfigAdmin.module.css';

const CONTRACT_CONSTANTS_UPDATE_MESSAGE = 'Contract constants has been updated';
const _UPLOADER_TYPES = 'uploader types';
const _UPLOADER_URL_NAME = 'uploader_type';
const acceptedRoles = [SUPERUSER];

const FormsDefaultsConfigAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        contractId: '',
        doubtFormIds: [],
        complianceActivityFormQuestionnaireId: '',
        defaultTeamId: '',
        endFormTemplateId: '',
        defaultEligibilityGroupId: '',
        weekendAsWorkingDay: false,
        bankHolidayAsWorkingDay: false,
        exitInterviewBookingPeriodDays: '',
        exitInterviewBookingDays: '',
        exitFormTemplateId: '',
        occThemeId: '',
        exitDocumentTypeId: '',
        exitDocumentEmailTemplateId: '',
        useExternalReferenceNumber: false
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState(initialErrorState);
    const [eligibilityGroups, setEligibilityGroups] = useState([]);
    const [serviceIdsForTeams, setServiceIdsForTeams] = useState([]);
    const [teams, setTeams] = useState([]);
    const [selectedUploaderIds, setSelectedUploaderIds] = useState([]);
    const [preSelectedUploaders, setPreSelectedUploaders] = useState([]);
    const [preSelectedUploaderIds, setPreSelectedUploaderIds] = useState([]);
    const [batchUploaderId, setBatchUploaderId] = useState('');
    const [showPostcodeUploaderModal, setShowPostcodeUploaderModal] = useState(false);
    const [showPurgeConfirmPrompt, setShowPurgeConfirmPrompt] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [keys, setKeys] = useState({
        doubtFormIds: '0',
        complianceActivityFormQuestionnaireId: '1',
        defaultTeamId: '2',
        exitFormTemplateId: '3',
        exitDocumentTypes: '4',
        exitDocumentEmailTemplateId: '5',
        endFormTemplateId: '6',
        defaultEligibilityGroupId: '7',
        uploaderTypes: '8',
        occThemeId: '9'
    });

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const eligibilityGroupDetails = useSelector(
        (state) => state.entities.participantService.eligibilityGroupSearch
    );
    const forms = useSelector((state) => state.entities.participantService.formsByContract);
    const services = useSelector((state) => state.entities.directusService.servicesByContractId);
    const uploaderTypes = useSelector((state) => state.entities.directusService.uploaderTypes);
    const occThemes = useSelector((state) => state.entities.directusService.occThemes);
    const teamDetails = useSelector((state) => state.entities.userService.teamsForService);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    const exitDocumentTypes = useSelector(
        (state) => state.entities.directusService.exitDocumentTypes
    );
    const exitEmailTemplates = useSelector(selectSortedCommunicationTemplates);

    // USE EFFECTS
    useEffect(() => {
        uploaderTypes?.length < 1 && dispatch(loadUploaderTypes());
        occThemes?.length < 1 && dispatch(loadOccThemeDetails());
        exitDocumentTypes?.length < 1 && dispatch(loadExitDocumentTypes());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (contractId) {
            dispatch(loadServiceDetailsForContractId(contractId));
            dispatch(searchEligibilityGroups({ contractId }));
            dispatch(loadFormsByContract(contractId));
            dispatch(loadContractConstants(contractId));
            dispatch(
                searchCommunicationTemplates({
                    type: 'Email',
                    contractIds: [contractId],
                    inactive: false
                })
            );
        }
    }, [contractId]);

    useEffect(() => {
        if (uploaderTypes?.length < 1) return;
        const { preSelects, preSelectIds } = updatePreSelects(
            _UPLOADER_URL_NAME,
            uploaderTypes,
            contractId
        );
        setPreSelectedUploaders(preSelects);
        setSelectedUploaderIds(preSelectIds);
        setPreSelectedUploaderIds(preSelectIds);
        setBatchUploaderId(
            uploaderTypes.find((el) => el.name.toLowerCase() === 'batch uploader')?.id
        );
    }, [uploaderTypes, contractId]);

    useEffect(() => {
        if (!(contractId in services)) return;
        const serviceIds = services[contractId].map((el) => el.id);
        if (serviceIds.length) {
            dispatch(loadTeamsForService({ serviceIds }));
            setServiceIdsForTeams(serviceIds);
        }
    }, [services]);

    useEffect(() => {
        setEligibilityGroups(
            eligibilityGroupDetails?.length && eligibilityGroupDetails[0].contractId === contractId
                ? eligibilityGroupDetails
                : []
        );
    }, [eligibilityGroupDetails]);

    useEffect(() => {
        const filteredTeams = teamDetails.length
            ? teamDetails.filter((el) => serviceIdsForTeams.includes(el.serviceId))
            : [];
        setTeams(filteredTeams);
    }, [teamDetails]);

    useEffect(() => {
        if (
            Object.keys(contractConstants).length < 1 ||
            contractConstants.contractId !== contractId
        )
            return;
        setNewEntry({
            ...contractConstants,
            exitInterviewBookingPeriodDays: contractConstants.exitInterviewBookingPeriodDays || '',
            exitInterviewBookingDays: contractConstants.exitInterviewBookingDays || ''
        });
    }, [contractConstants]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage.includes(`Updating ${_UPLOADER_TYPES} collection`)) {
            dispatch(loadUploaderTypes());
        }
        if (successMessage === CONTRACT_CONSTANTS_UPDATE_MESSAGE)
            dispatch(loadContractConstants(contractId));

        setIsButtonDisabled(false);
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (!contractId) return NO_CONTRACT_ERROR;
        if (!forms) return FORMS_ERROR;
    };

    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    const confirmPostcodePurge = (_, doPurge) => {
        if (doPurge) onPostcodesPurge();
        setShowPurgeConfirmPrompt(false);
    };

    const cleanUp = () => {
        dispatch(clearTeamsForService());
        setServiceIdsForTeams([]);
        setKeys(clearKeys(keys));
        setNewEntry(initialState);
        setEligibilityGroups([]);
        setTeams([]);
        setSelectedUploaderIds([]);
        setPreSelectedUploaders([]);
        setPreSelectedUploaderIds([]);
        setBatchUploaderId('');
        setErrors({});
        setIsButtonDisabled(false);
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onContractDoubtFormsChange = (chosenIds) => {
        setNewEntry((prev) => ({
            ...prev,
            doubtFormIds: chosenIds || []
        }));
    };

    const onComplianceActivityFormQuestionnaireChange = (chosenId) => {
        setNewEntry((prev) => ({
            ...prev,
            complianceActivityFormQuestionnaireId: chosenId || ''
        }));
    };

    const onDefaultTeamChange = (chosenId) => {
        clearError('defaultTeamId');
        const defaultTeam = teams.find((el) => el.id === chosenId);
        setNewEntry((prev) => ({
            ...prev,
            defaultTeamId: chosenId || '',
            defaultTeam: defaultTeam || {}
        }));
    };

    const onExitFormTemplateChange = (chosenId) => {
        clearError('exitFormTemplateId');
        const exitFormTemplate = forms.find((el) => el.id === chosenId);
        setNewEntry((prev) => ({
            ...prev,
            exitFormTemplateId: chosenId || '',
            exitFormTemplate: exitFormTemplate || {}
        }));
    };

    const onExitDocumentTypeChange = (chosenId) => {
        clearError('exitDocumentTypeId');
        const exitDocumentType = exitDocumentTypes.find((el) => el.id === chosenId);
        setNewEntry((prev) => ({
            ...prev,
            exitDocumentTypeId: chosenId || '',
            exitDocumentType: exitDocumentType || {}
        }));
    };

    const onExitDocumentEmailTemplateChange = (chosenId) => {
        clearError('exitDocumentEmailTemplateId');
        const exitEmailTemplate = exitEmailTemplates.find((el) => el.id === chosenId);
        setNewEntry((prev) => ({
            ...prev,
            exitDocumentEmailTemplateId: chosenId || '',
            exitEmailTemplate: exitEmailTemplate || {}
        }));
    };

    const onExitInterviewBookingPeriodDaysChange = (e) => {
        clearError('exitInterviewBookingPeriodDays');
        clearError('exitInterviewBookingDays');
        setNewEntry((prev) => ({
            ...prev,
            exitInterviewBookingPeriodDays: e.target.value.replace(/[^\d]/g, '')
        }));
    };

    const onExitInterviewBookingDaysChange = (e) => {
        clearError('exitInterviewBookingDays');
        clearError('exitInterviewBookingPeriodDays');
        setNewEntry((prev) => ({
            ...prev,
            exitInterviewBookingDays: e.target.value.replace(/[^\d]/g, '')
        }));
    };

    const onEndFormTemplateChange = (chosenId) => {
        clearError('endFormTemplateId');
        setNewEntry((prev) => ({
            ...prev,
            endFormTemplateId: chosenId || ''
        }));
    };

    const onDefaultEligibilityGroupChange = (chosenId) => {
        clearError('defaultEligibilityGroupId');
        setNewEntry((prev) => ({
            ...prev,
            defaultEligibilityGroupId: chosenId || ''
        }));
    };

    const onPostcodeUploaderOpen = () => {
        setShowPostcodeUploaderModal(true);
    };

    const onPostcodeUploaderModalClose = (e, reason) => {
        if (reason && reason === 'backdropClick') return;
        setShowPostcodeUploaderModal(false);
    };

    const onCancelPostcodeUploaderModal = (e, reason) => {
        if (reason && reason === 'backdropClick') return;
        setShowPostcodeUploaderModal(false);
    };

    const onPostcodesPurge = () => {
        const message = `Postcode data for contractId: ${contractId} has been deleted`;
        dispatch(deletePostcodeCsvData(contractId, message));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const {
            /* eslint-disable no-unused-vars */
            defaultTeam,
            defaultEligibilityGroup,
            contractDoubtForm,
            complianceActivityFormQuestionnaire,
            endFormTemplate,
            exitFormTemplate,
            exitInterviewBookingPeriodDays,
            exitInterviewBookingDays,
            ...rest
        } = newEntry;
        const values = {
            ...rest,
            exitInterviewBookingPeriodDays:
                exitInterviewBookingPeriodDays === '' ? null : exitInterviewBookingPeriodDays,
            exitInterviewBookingDays:
                exitInterviewBookingDays === '' ? null : exitInterviewBookingDays
        };
        const { newErrors } = validate(
            values,
            contractConstants,
            selectedUploaderIds,
            preSelectedUploaderIds
        );
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const payload = { ...values, contractId };
        dispatch(updateContractConstants(payload, CONTRACT_CONSTANTS_UPDATE_MESSAGE));
        updateSelectedItems(
            _UPLOADER_URL_NAME,
            uploaderTypes,
            selectedUploaderIds,
            contractId,
            _UPLOADER_TYPES,
            dispatch
        );
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <>
            <form className={form.formWrapper} onSubmit={onSubmit} data-testid="form_start">
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <MultiSelect
                            id="doubtFormIds"
                            key={keys.doubtFormIds}
                            label="Contract Doubt Forms"
                            placeholder="Select forms"
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={forms || []}
                            preSelectedIds={newEntry.doubtFormIds || []}
                            preSelects={
                                forms.filter((el) => newEntry.doubtFormIds.includes(el.id)) || []
                            }
                            onChange={(chosenIds) => onContractDoubtFormsChange(chosenIds)}
                        />

                        <SingleSelect
                            id="complianceActivityFormQuestionnaireId"
                            key={keys.complianceActivityFormQuestionnaireId}
                            label="Compliance Activity Form Questionnaire"
                            placeholder="Select a form"
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={forms || []}
                            selectedId={newEntry.complianceActivityFormQuestionnaireId || ''}
                            selected={
                                forms.find(
                                    (el) => el.id === newEntry.complianceActivityFormQuestionnaireId
                                ) || {}
                            }
                            onChange={(chosenId) =>
                                onComplianceActivityFormQuestionnaireChange(chosenId)
                            }
                        />

                        <SingleSelect
                            id="defaultTeamId"
                            key={keys.defaultTeamId}
                            label="Default Team"
                            placeholder="Select a default team"
                            disabled={!hasRole(acceptedRoles, roles)}
                            mandatory={true}
                            menuItems={teams || []}
                            selectedId={newEntry.defaultTeamId || ''}
                            selected={teams.find((el) => el.id === newEntry.defaultTeamId) || {}}
                            error={errors.defaultTeamId}
                            onChange={(chosenId) => onDefaultTeamChange(chosenId)}
                        />

                        <SingleSelect
                            id="exitFormTemplateId"
                            key={keys.exitFormTemplateId}
                            label="Exit Form Template"
                            placeholder="Select a form"
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={forms || []}
                            selectedId={newEntry.exitFormTemplateId || ''}
                            selected={
                                forms.find((el) => el.id === newEntry.exitFormTemplateId) || {}
                            }
                            onChange={(chosenId) => onExitFormTemplateChange(chosenId)}
                        />

                        <SingleSelect
                            id="exitDocumentTypeId"
                            key={keys.exitDocumentTypeId}
                            label="Exit Document"
                            placeholder="Select a document"
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={exitDocumentTypes || []}
                            selectedId={newEntry.exitDocumentTypeId || ''}
                            selected={
                                exitDocumentTypes?.find(
                                    (el) => el.id === newEntry.exitDocumentTypeId
                                ) || {}
                            }
                            onChange={(chosenId) => onExitDocumentTypeChange(chosenId)}
                        />

                        <SingleSelect
                            id="exitDocumentEmailTemplateId"
                            key={keys.exitDocumentEmailTemplateId}
                            label="Exit Document Email Template"
                            placeholder="Select an email template"
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={exitEmailTemplates || []}
                            selectedId={newEntry.exitDocumentEmailTemplateId || ''}
                            selected={
                                exitEmailTemplates?.find(
                                    (el) => el.id === newEntry.exitDocumentEmailTemplateId
                                ) || {}
                            }
                            onChange={(chosenId) => onExitDocumentEmailTemplateChange(chosenId)}
                        />

                        <TextInputField
                            id={'exitInterviewBookingPeriodDays'}
                            label={'Exit Interview Booking Period Days'}
                            placeholder={'Enter number of days'}
                            disabled={!hasRole(acceptedRoles, roles)}
                            maxLength={3}
                            value={String(newEntry.exitInterviewBookingPeriodDays) || ''}
                            error={errors.exitInterviewBookingPeriodDays}
                            onChange={(e) => onExitInterviewBookingPeriodDaysChange(e)}
                        />

                        <TextInputField
                            id={'exitInterviewBookingDays'}
                            label={'Exit Interview Booking Days'}
                            placeholder={'Enter number of days'}
                            disabled={!hasRole(acceptedRoles, roles)}
                            maxLength={3}
                            value={String(newEntry.exitInterviewBookingDays) || ''}
                            error={errors.exitInterviewBookingDays}
                            onChange={(e) => onExitInterviewBookingDaysChange(e)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="occTheme"
                            key={keys.occThemeId}
                            label="Online Career Centre Theme"
                            placeholder="Select a theme"
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={occThemes || []}
                            selectedId={newEntry.occThemeId || ''}
                            selected={occThemes.find((el) => el.id === newEntry.occThemeId) || {}}
                            onChange={(chosenId) => onUpdate('occThemeId', chosenId)}
                        />
                        <SingleSelect
                            id="endFormTemplateId"
                            key={keys.endFormTemplateId}
                            label="End Form Template"
                            placeholder="Select a form"
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={forms || []}
                            selectedId={newEntry.endFormTemplateId || ''}
                            selected={
                                forms.find((el) => el.id === newEntry.endFormTemplateId) || {}
                            }
                            onChange={(chosenId) => onEndFormTemplateChange(chosenId)}
                        />

                        <SingleSelect
                            id="defaultEligibilityGroupId"
                            key={keys.defaultEligibilityGroupId}
                            label="Default Eligibility Group"
                            placeholder="Select a default eligibility group"
                            disabled={!hasRole(acceptedRoles, roles)}
                            mandatory={true}
                            menuItems={eligibilityGroups || []}
                            selectedId={newEntry.defaultEligibilityGroupId || ''}
                            selected={
                                eligibilityGroups.find(
                                    (el) => el.id === newEntry.defaultEligibilityGroupId
                                ) || {}
                            }
                            error={errors.defaultEligibilityGroupId}
                            onChange={(chosenId) => onDefaultEligibilityGroupChange(chosenId)}
                        />

                        <MultiSelect
                            id="uploaders"
                            key={keys.uploaderTypes}
                            label="Uploaders"
                            placeholder="Select uploaders"
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={uploaderTypes || []}
                            preSelectedIds={preSelectedUploaderIds}
                            preSelects={preSelectedUploaders}
                            onChange={(chosenIds) => setSelectedUploaderIds(chosenIds)}
                        />

                        {selectedUploaderIds.includes(batchUploaderId) && (
                            <div className={local.twinButtonWrapper}>
                                <Button
                                    id="postcodeUploaderButton"
                                    type="button"
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    content="Attach Postcode Uploader"
                                    icon={true}
                                    onClick={onPostcodeUploaderOpen}
                                />

                                <Button
                                    id="purgePostcodesButton"
                                    type="button"
                                    content="Purge Postcodes"
                                    onClick={() => setShowPurgeConfirmPrompt(true)}
                                />
                            </div>
                        )}

                        <RadioButtons
                            id="weekendAsWorkingDay"
                            label="Are Weekends Working Days?"
                            disabled={!hasRole(acceptedRoles, roles)}
                            value={newEntry.weekendAsWorkingDay || false}
                            onChange={(option) => onUpdate('weekendAsWorkingDay', option)}
                        />

                        <RadioButtons
                            id="bankHolidayAsWorkingDay"
                            label="Are Bank Holidays Working Days?"
                            disabled={!hasRole(acceptedRoles, roles)}
                            value={newEntry.bankHolidayAsWorkingDay || false}
                            onChange={(option) => onUpdate('bankHolidayAsWorkingDay', option)}
                        />

                        <RadioButtons
                            id="useExternalReferenceNumber"
                            label="Use External Reference Number"
                            disabled={!hasRole(acceptedRoles, roles)}
                            value={newEntry.useExternalReferenceNumber || false}
                            onChange={(option) => onUpdate('useExternalReferenceNumber', option)}
                        />
                    </div>
                </div>

                <Button
                    id="formsDefaultsConfigAdminButton"
                    content="UPDATE FORMS, DEFAULTS & CONFIGURATION"
                    disabled={!hasRole(acceptedRoles, roles) || isButtonDisabled}
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
            {showPostcodeUploaderModal && (
                <PostcodeUploader
                    contractId={contractId}
                    onClose={onPostcodeUploaderModalClose}
                    onCancel={onCancelPostcodeUploaderModal}
                    isOpen={showPostcodeUploaderModal}
                />
            )}
            {showPurgeConfirmPrompt && (
                <ConfirmPrompt
                    isOpen={showPurgeConfirmPrompt}
                    onCancel={(e) => confirmPostcodePurge(e, false)}
                    onConfirm={(e) => confirmPostcodePurge(e, true)}
                    content="By clicking 'Yes', all postcodes for this contract will be purged. This action cannot be undone. Are you sure you want to continue?"
                    customClass="formatContent"
                />
            )}
        </>
    );
};

FormsDefaultsConfigAdmin.propTypes = {
    contractId: PropTypes.string
};

export default FormsDefaultsConfigAdmin;
